import { useCallback, useState } from 'react';

import { Input, type InputProps } from '@veraclins-dev/ui/input';
import { INPUT_CLASS_OVERRIDES } from '@veraclins-dev/ui/shared-classes';
import { cn, mergeDescribedBy } from '@veraclins-dev/utils';

import {
  type BaseInputProps,
  getInputProps,
  type InputFieldProps,
  useFieldProperties,
} from '../../field-helpers';
import { InputFieldWrapper } from '../input-field-wrapper';

export interface TextFieldProps extends InputProps, BaseInputProps {
  inputRef?: React.Ref<HTMLInputElement>;
  type?: InputFieldProps['type'];
  /**
   * Render only the input — no wrapper, label, or error chrome. Use with an
   * externally-rendered `<Label htmlFor={field.id}>` for compact custom layouts.
   */
  bare?: boolean;
}

/**
 * A text input bound to a form field, with its label and validation errors.
 *
 * `surface="card"` fills the whole field box with the card colour, for a
 * field placed on a grey (muted) section.
 *
 * A caller's `aria-describedby` is kept, and the error list id is appended
 * while the field has errors.
 */
export const TextField = ({
  name,
  label,
  leftIcon,
  rightIcon,
  type = 'text',
  labelProps,
  topText,
  className,
  inputRef,
  field,
  inputClass,
  rightAddon,
  wrapperClassName,
  bare,
  id: idProp,
  ref,
  'aria-describedby': describedBy,
  ...props
}: TextFieldProps) => {
  const isPassword = type === 'password';
  const [hidden, setHidden] = useState(isPassword);
  const toggleHidden: React.MouseEventHandler<SVGElement> = useCallback(
    (e) => {
      e.preventDefault();
      setHidden(!hidden);
    },
    [hidden],
  );
  const { errorId, id: fieldId } = useFieldProperties(field);
  const id = idProp ?? fieldId;
  const passwordIcon = hidden ? 'eye-open' : 'eye-slash';

  const { key, ...inputProps } = getInputProps({ field, type, name, id });

  const input = (
    <Input
      {...props}
      {...inputProps}
      key={key}
      ref={inputRef}
      id={id}
      aria-label={typeof label === 'string' ? label : bare ? undefined : name}
      leftIcon={leftIcon}
      rightIcon={
        isPassword
          ? {
              name: passwordIcon,
              size: 'sm',
              className: 'cursor-pointer',
              onClick: toggleHidden,
            }
          : rightIcon
      }
      aria-describedby={mergeDescribedBy(describedBy, errorId)}
      aria-invalid={errorId ? true : undefined}
      type={isPassword && !hidden ? 'text' : type}
      className={cn(INPUT_CLASS_OVERRIDES, inputClass, bare && className)}
    />
  );

  if (bare) {
    return input;
  }

  return (
    <InputFieldWrapper
      className={cn('gap-2', className)}
      field={field}
      label={label}
      labelProps={labelProps}
      topText={topText}
      wrapperClassName={wrapperClassName}
      ref={ref}
      inputProps={props}
    >
      {input}
      {rightAddon}
    </InputFieldWrapper>
  );
};
